import { makeHttpClient, _unwrapAxiosResponse } from "./http";
import { handleFileDownload } from "../utils/fileDownloadHelper";
import env from "../environment/env.json";
import { ToastProgrammatic as Toast } from "buefy";
import { DialogProgrammatic as Dialog } from "buefy";
/* eslint-disable camelcase, @typescript-eslint/camelcase */
export interface ReportTracker {
  id?: number;
  period: string;
  planned_scheme_deadline: string;
  status: string;
  revised_scheme_deadline: string;
  final_dataset_delivery_date: string;
  actual_submission_date: string;
  proof_of_submission: string;
  comments: string;
  comments_external: string;
  date_of_resubmission: string;
  data_upload_comment: string;
}

export interface ReportResponse {
  id: number;
  report_type: string;
  frequency: string;
  country: number;
  scheme: number;
  stream: number;
  report_tracker: Array<ReportTracker>;
}
/* eslint-enable camelcase, @typescript-eslint/camelcase */

function dateFormatter(dt: Date) {
  if (dt != null) {
    return dt.toLocaleDateString("fr-CA");
  }
  return dt;
}

export class ReportingTrackerDataService {
  private httpClient = makeHttpClient();

  getAll(query: string, customer_id: number) {
    if (customer_id != null) {
      query = query + `&switch_customer_id=${customer_id}`;
    }
    return _unwrapAxiosResponse(
      this.httpClient.get<ReportResponse>("/reporting/trackerRead/?" + query)
    );
  }

  getAllReports() {
    return _unwrapAxiosResponse(
      this.httpClient.get<ReportResponse>(`${env.reportingUrl}reports/`)
    );
  }

  getOneOffReportsOnly(schemeId: number, streamId: number) {
    const frequency = "One-off/other (see additional information)";
    let url = `${env.reportingUrl}reports/?frequency=${frequency}`;

    if (schemeId != null && streamId != null) {
      url = url + "&scheme=" + schemeId + "&stream=" + streamId;
    }
    return _unwrapAxiosResponse(this.httpClient.get<ReportResponse>(url));
  }

  getReportBySchemeStream(
    schemeId: number,
    streamId: number,
    parentReportId: number,
    exclude_one_off_reports: boolean
  ) {
    let url;
    if (schemeId != null && streamId != null) {
      url =
        `${env.reportingUrl}reports/` +
        "?scheme=" +
        schemeId +
        "&stream=" +
        streamId +
        "&exclude_one_off_reports=" +
        exclude_one_off_reports;
      if (parentReportId != null) {
        url = url + "&parent_report_id=" + parentReportId;
      }
    } else {
      url = `${env.reportingUrl}reports/`;
    }
    return _unwrapAxiosResponse(this.httpClient.get<ReportResponse>(url));
  }

  create(form: any, file: any, dataUpload: any) {
    const headers = {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    };

    const data = new FormData();
    data.append("id", form.id);
    data.append("report_id", form.report_id);
    data.append("report_type", form.report_type);
    data.append("customer_id", form.customer_id);
    data.append(
      "customer_account_manager_id",
      form.customer_account_manager_id
    );
    data.append("country", form.country_id);
    data.append("stream", form.stream_id);
    data.append("scheme", form.scheme_id);
    data.append("period", form.period);

    if (form.reporting_responsible != null) {
      data.append("reporting_responsible", form.reporting_responsible);
    }
    if (form.actual_submission_date != null) {
      data.append(
        "actual_submission_date",
        dateFormatter(form.actual_submission_date)
      );
    }
    if (form.planned_scheme_deadline != null) {
      data.append(
        "planned_scheme_deadline",
        dateFormatter(form.planned_scheme_deadline)
      );
    }
    if (form.revised_scheme_deadline != null) {
      data.append(
        "revised_scheme_deadline",
        dateFormatter(form.revised_scheme_deadline)
      );
    }
    if (form.final_dataset_delivery_date != null) {
      data.append(
        "final_dataset_delivery_date",
        dateFormatter(form.final_dataset_delivery_date)
      );
    }
    if (form.status != null) {
      data.append("status", form.status);
    }
    if (form.resubmitted != null) {
      data.append("resubmitted", form.resubmitted);
    }
    if (form.date_of_resubmission != null) {
      data.append(
        "date_of_resubmission",
        dateFormatter(form.date_of_resubmission)
      );
    }
    if (form.landbell_entity != null) {
      data.append("landbell_entity", form.landbell_entity);
    }
    if (form.comments != null) {
      data.append("comments", form.comments);
    }
    if (form.proof_of_submission != null) {
      data.append("proof_of_submission", form.proof_of_submission);
    }
    if (form.comments_external != null) {
      data.append("comments_external", form.comments_external);
    }
    if (form.data_upload_comment != null) {
      data.append("data_upload_comment", form.data_upload_comment);
    }
    if (file != null) {
      for (let i = 0; i < file.length; i++) {
        data.append("file" + i, file[i]);
      }
    }
    data.append("file", file);
    if (dataUpload != null) {
      for (let i = 0; i < dataUpload.length; i++) {
        data.append("data_upload" + i, dataUpload[i]);
      }
    }
    data.append("data_upload", dataUpload);
    return _unwrapAxiosResponse(
      this.httpClient.post<ReportResponse>(
        env.reportingTrackerUrl,
        data,
        headers
      )
    );
  }

  update(form: any, file: any, dataUpload: any) {
    /** eslint-enable camelcase, @typescript-eslint/camelcase */
    const data = new FormData();
    const headers = {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    };
    if (!form.customer_id) {
      throw new Error("Customer id is required");
    }
    data.append("id", form.id);
    data.append("customer_scheme", form.customer_scheme);
    data.append("period", form.period);
    data.append("customer_id", form.customer_id);
    data.append(
      "customer_account_manager_id",
      form.customer_account_manager_id
    );
    data.append("country", form.country_id);
    data.append("stream", form.stream_id);

    if (form.reporting_responsible != null) {
      data.append("reporting_responsible", form.reporting_responsible);
    }
    if (form.actual_submission_date != null) {
      data.append(
        "actual_submission_date",
        dateFormatter(form.actual_submission_date)
      );
    }
    if (form.planned_scheme_deadline != null) {
      data.append(
        "planned_scheme_deadline",
        dateFormatter(form.planned_scheme_deadline)
      );
    }
    if (form.revised_scheme_deadline != null) {
      data.append(
        "revised_scheme_deadline",
        dateFormatter(form.revised_scheme_deadline)
      );
    }
    if (form.final_dataset_delivery_date != null) {
      data.append(
        "final_dataset_delivery_date",
        dateFormatter(form.final_dataset_delivery_date)
      );
    }
    if (form.status != null) {
      data.append("status", form.status);
    }
    if (form.resubmitted != null) {
      data.append("resubmitted", form.resubmitted);
    }
    if (form.date_of_resubmission != null) {
      data.append(
        "date_of_resubmission",
        dateFormatter(form.date_of_resubmission)
      );
    }
    if (form.landbell_entity != null) {
      data.append("landbell_entity", form.landbell_entity);
    }
    if (form.comments != null) {
      data.append("comments", form.comments);
    }
    if (form.proof_of_submission != null) {
      data.append(
        "proof_of_submission",
        form.proof_of_submission.split("/").pop()
      );
    }
    if (form.comments_external != null) {
      data.append("comments_external", form.comments_external);
    }
    if (form.data_upload_comment != null) {
      data.append("data_upload_comment", form.data_upload_comment);
    }
    if (form.pos_document.length > 0) {
      data.append("pos_document", JSON.stringify(form.pos_document));
    }

    if (form.data_document && form.data_document.length > 0) {
      data.append("data_document", JSON.stringify(form.data_document));
    }

    if (file != null) {
      for (let i = 0; i < file.length; i++) {
        data.append("file" + i, file[i]);
      }
    }
    data.append("file", file);

    if (dataUpload != null) {
      for (let i = 0; i < dataUpload.length; i++) {
        data.append("data_upload" + i, dataUpload[i]);
      }
    }
    data.append("data_upload", dataUpload);

    return _unwrapAxiosResponse(
      this.httpClient.put<ReportResponse>(
        env.reportingTrackerUrl + `${form.id}/`,
        data,
        headers
      )
    );
  }

  bulkUpdateReportingResponsible(
    ids: number[],
    selectedfilters: string,
    reporting_responsible: number,
    checkAll: boolean
  ) {
    const url = `${env.reportingUrl}bulk/responsible/?` + selectedfilters;
    const data = {
      reporting_responsible: reporting_responsible,
      filters: selectedfilters,
      records: ids,
      all_records: checkAll
    };
    return _unwrapAxiosResponse(
      this.httpClient.post<ReportResponse>(url, data)
    );
  }

  bulkUpdateWorkflowStatus(
    ids: number[],
    selectedfilters: string,
    checkAll: boolean,
    status: string
  ) {
    const url = `${env.reportingUrl}bulk/status/?` + selectedfilters;
    const data = {
      status: status,
      records: ids,
      all_records: checkAll
    };
    return _unwrapAxiosResponse(this.httpClient.post(url, data));
  }

  bulkAddComment(
    ids: number[],
    selectedfilters: string,
    checkAll: boolean,
    comment: string,
    comment_type: string
  ) {
    const url = `${env.reportingUrl}bulk/comment/?` + selectedfilters;
    const data = {
      comment: comment,
      records: ids,
      all_records: checkAll,
      comment_type: comment_type
    };
    return _unwrapAxiosResponse(this.httpClient.post(url, data));
  }

  exportProofOfSubmission(id: number, fileName: string) {
    const url = `${
      env.reportingUrl
    }export/document?id=${id}&proof_of_submission=${encodeURIComponent(
      fileName
    )}`;

    return handleFileDownload(
      this.httpClient,
      url,
      fileName,
      "Export not possible",
      "Not able to download proof of submission, please contact System Admin"
    );
  }

  exportPos(
    ids: number[],
    selectedfilters: string,
    checkAll: boolean,
    switchCustomerId: any
  ) {
    const params = new URLSearchParams();
    let switchCustomerParam = "";
    if (switchCustomerId != null) {
      switchCustomerParam = `&switch_customer_id=${switchCustomerId}`;
    }
    if (ids.length == 0) {
      throw new Error("Please select at least one record.");
    } else {
      ids.map(id => {
        params.append("records", "" + id);
      });
      const url =
        `${env.reportingUrl}export/document/pos?${params.toString()}` +
        switchCustomerParam +
        "&" +
        selectedfilters +
        "&all_records=" +
        checkAll;
      const successToast = Toast.open({
        indefinite: true,
        message: "Download in Progress!",
        type: "is-success"
      });
      return _unwrapAxiosResponse(
        this.httpClient.get(url, { responseType: "blob" }).then(
          ({ data }) => {
            const downloadUrl = window.URL.createObjectURL(new Blob([data]));

            const link = document.createElement("a");

            link.href = downloadUrl;

            link.setAttribute("download", "reporting-tracker-pos.zip"); //any other extension

            document.body.appendChild(link);
            link.click();
            link.remove();
            successToast.close();
          },
          err => {
            Dialog.alert({
              title: "Export not possible",
              message:
                "Not able to download proof of submission, please contact System Admin",
              type: "is-danger",
              ariaRole: "alertdialog",
              ariaModal: true
            });
            successToast.close();
            return err;
          }
        )
      );
    }
  }

  exportReport(
    ids: number[],
    selectedfilters: string,
    checkAll: boolean,
    switchCustomerId: any
  ) {
    const params = new URLSearchParams();
    let switchCustomerParam = "";
    if (switchCustomerId != null) {
      switchCustomerParam = `&switch_customer_id=${switchCustomerId}`;
    }
    if (ids.length == 0) {
      throw new Error("Please select at least one record.");
    } else {
      if (!checkAll) {
        ids.map(id => {
          params.append("records", "" + id);
        });
      }
      const url =
        `${env.reportingUrl}export-reporting-tracker/?${params.toString()}` +
        switchCustomerParam +
        "&" +
        selectedfilters +
        "&all_records=" +
        checkAll;
      const successToast = Toast.open({
        indefinite: true,
        message: "Download in Progress!",
        type: "is-success"
      });
      return _unwrapAxiosResponse(
        this.httpClient.get(url, { responseType: "blob" }).then(
          ({ data }) => {
            const downloadUrl = window.URL.createObjectURL(new Blob([data]));

            const link = document.createElement("a");

            link.href = downloadUrl;

            link.setAttribute("download", "reporting_tracker.xlsx"); //any other extension

            document.body.appendChild(link);

            link.click();

            link.remove();
            successToast.close();
          },
          async err => {
            let errorMessage =
              "Not able to download reporting tracker, please contact System Admin";

            // Try to extract error message from backend response
            if (err?.response?.data) {
              // If the error response is a Blob (common with responseType: "blob")
              if (err.response.data instanceof Blob) {
                try {
                  const text = await err.response.data.text();
                  const jsonError = JSON.parse(text);
                  errorMessage =
                    jsonError.detail ||
                    jsonError.error ||
                    jsonError.message ||
                    errorMessage;
                } catch (e) {
                  // If parsing fails, keep the default message
                }
              } else if (typeof err.response.data === "object") {
                errorMessage =
                  err.response.data.detail ||
                  err.response.data.error ||
                  err.response.data.message ||
                  errorMessage;
              }
            } else if (err?.message) {
              errorMessage = err.message;
            }

            Dialog.alert({
              title: "Export not possible",
              message: errorMessage,
              type: "is-danger",
              ariaRole: "alertdialog",
              ariaModal: true
            });
            successToast.close();
            return err;
          }
        )
      );
    }
  }

  exportBulkReport(
    date_type: any,
    start_date: any,
    end_date: any,
    customersList: any
  ) {
    if (date_type == null) {
      throw new Error("Please choose the date type.");
    }
    if (start_date == null || end_date == null) {
      throw new Error("Please choose the valid date range.");
    }
    const url =
      `${env.reportingUrl}bulk/export/all/tracker-data?date_type=` +
      date_type +
      `&start_date=` +
      dateFormatter(start_date) +
      "&end_date=" +
      dateFormatter(end_date) +
      "&customers=" +
      customersList;
    const successToast = Toast.open({
      indefinite: true,
      message: "Download in Progress!",
      type: "is-success"
    });
    this.httpClient
      .get(url, { responseType: "blob" })
      .then(({ data }) => {
        const downloadUrl = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", "reporting_tracker.xlsx"); //any other extension
        document.body.appendChild(link);
        link.click();
        link.remove();
        successToast.close();
      })
      .catch(err => {
        Dialog.alert({
          title: "Export not possible",
          message:
            "Not able to download bulk report, please contact System Admin",
          type: "is-danger",
          ariaRole: "alertdialog",
          ariaModal: true
        });
        successToast.close();
        return err;
      });
  }

  reviseSchemeDeadline(
    ids: number[],
    deadline: any,
    isAllChecked: boolean,
    filters: any
  ) {
    if (deadline == null) {
      throw new Error("Please choose the date.");
    }
    const url = `${env.reportingUrl}revise-scheme-deadline/?` + filters;
    const data = {
      revised_scheme_deadline: dateFormatter(deadline),
      records: ids,
      select_all: isAllChecked,
      filter_params: filters
    };
    return _unwrapAxiosResponse(
      this.httpClient.post<ReportResponse>(url, data)
    );
  }

  deliveringFinalDatasetDeadline(
    ids: number[],
    deadline: any,
    selectedfilters: string,
    checkAll: boolean
  ) {
    if (deadline == null) {
      throw new Error("Please choose the date.");
    }
    const url =
      `${env.reportingUrl}final-dataset-delivery-deadline/?` + selectedfilters;
    const data = {
      final_dataset_delivery_date: dateFormatter(deadline),
      records: ids,
      all_records: checkAll
    };
    return _unwrapAxiosResponse(
      this.httpClient.post<ReportResponse>(url, data)
    );
  }

  getReportById(id: any, switchCustomerId: any) {
    return _unwrapAxiosResponse(
      this.httpClient.get<ReportResponse>(
        env.reportingTrackerUrl +
          `${id}/?&switchCustomerEnabled=${switchCustomerId}`
      )
    );
  }

  getReportingTrackerHistory(id: number) {
    return _unwrapAxiosResponse(
      this.httpClient.get<ReportResponse>(
        `${env.reportingTrackerUrl}${id}/history/`
      )
    );
  }
}

export default ReportingTrackerDataService;
